import { isObject } from 'lodash-es'
import { z } from 'zod'
import {
  CountryCode,
  allCountries,
  UNLIMITED_COUNTRY_CODE,
} from '@/constants/country-code'
import { ModelServiceWithEndAt } from '@/types/mock-api-types'
import { Nullable } from '@/types/nullable'

const AllCountryCodeSchema = z
  .nativeEnum(CountryCode)
  .or(z.literal(UNLIMITED_COUNTRY_CODE))

type AllCountryCode = z.infer<typeof AllCountryCodeSchema>

// 國家預設值會依序優先使用
// 1. 有買的地區資料庫之中，擁有當前工作區的國家地區（最高權重，以下次之）
// 2. 若有買地區資料庫，則使用陣列的第一個地區
// 3. 使用當前工作區的國家
// 4. 若以上皆非，使用不限國家 or 台灣（不太確定啥情境會進來）
export function getDefaultCountryCode(
  checkCode: unknown,
  workspaceCountryCode?: Nullable<CountryCode>,
  availableServices?: ModelServiceWithEndAt[] | CountryCode[],
  hasUnlimited?: boolean,
): AllCountryCode {
  const usableCountries =
    (isModelServiceWithEndAt(availableServices)
      ? availableServices
          ?.filter((service) => service.serviceCategory === 'database')
          .map((service) => service.code?.split('_')[1] ?? '')
      : availableServices) ?? []
  const ownCountry = workspaceCountryCode ?? ''
  const defaultPaidCountry =
    usableCountries.length > 0 ? usableCountries[0] : null
  const defaultPaidOwnCountry =
    usableCountries.find(
      (usableCountry) => usableCountry === ownCountry && usableCountry !== '',
    ) ?? null

  const defaultOwnCountry = allCountries.find(
    (country) => country.value === ownCountry,
  )?.value

  const defaultCode =
    defaultPaidOwnCountry ??
    defaultPaidCountry ??
    defaultOwnCountry ??
    (hasUnlimited ? UNLIMITED_COUNTRY_CODE : 'tw')

  const result = usableCountries.some(
    (usableCountry) => usableCountry === checkCode,
  )
    ? (checkCode as string)
    : defaultCode

  return AllCountryCodeSchema.parse(result)
}

const isModelServiceWithEndAt = (
  availableServices?: ModelServiceWithEndAt[] | CountryCode[],
): availableServices is ModelServiceWithEndAt[] =>
  !!availableServices?.every(
    (service) => isObject(service) && 'endAt' in service,
  )
